import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { t } from '@hefe/i18n';
import { BasketballGameStatus, UserRole } from '@hefe/models';
import {
  BasketballGameActions,
  BasketballGamesSlice,
  ModalsSlice,
  currentUserIsAdminSelector,
  userState,
} from '@hefe/store';
import { dateFormat, timeFormat } from '@hefe/utils';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tabs,
  Typography,
} from 'antd';
import { Gutter } from 'antd/lib/grid/row';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import AddEditGameForm from '../../../team/forms/add-game/add-edit-game.form';
import EventDetailsAuditsComponent from '../event-details/event-details-audits/event-details-audits.component';
import GameDetailsAdminComponent from './game-details-admin/game-details-admin.component';
import GameDetailsClubComponent from './game-details-club/game-details-club.component';
import GameDetailsTeamComponent from './game-details-team/game-details-team.component';
import GameDetailsUpdateRefereeComponent from './game-details-update-referee/game-details-update-referee.component';

export default function GameDetailsComponent() {
  const dispatch = useAppDispatch();

  const [deleting, setDeleting] = useState(false);

  const user = useSelector(userState).user;
  const userIsAdmin = useSelector(currentUserIsAdminSelector);
  const gameState = useSelector(BasketballGamesSlice.basketballGamesState);
  const gameModal = useSelector(ModalsSlice.modalsState).gameModal;

  const gameId = gameModal.id;

  const game = gameState.gameDetails[gameId];

  useEffect(() => {
    if (gameId && gameId > 0 && !game) {
      fetchGame();
    }
  }, [gameId]);

  const fetchGame = () => {
    if (gameId && gameId > 0) {
      dispatch(BasketballGamesSlice.fetchBasketballGame(gameId));
    }
  };

  const deleteGame = () => {
    if (gameId && gameId > 0) {
      setDeleting(true);
      dispatch(BasketballGameActions.deleteBasketballGame(gameId))
        .unwrap()
        .then(() => {
          dispatch(ModalsSlice.modalsActions.closeGameModal());
        })
        .catch(() => undefined)
        .finally(() => {
          setDeleting(false);
        });
    }
  };

  const editGame = () => {
    if (game && gameId) {
      dispatch(
        ModalsSlice.modalsActions.openModal({
          component: <AddEditGameForm gameId={gameId} />,
          title: t('games.edit'),
          formId: `add-edit-game-form-${gameId || 'new'}`,
          zIndex: 99999,
        })
      );
    }
  };

  const gutter = [16, 16] as [Gutter, Gutter];

  const actionButtons = useMemo(() => {
    const returnValue = [];

    returnValue.push(
      <Button onClick={() => fetchGame()} icon={<ReloadOutlined />} />
    );

    if (userIsAdmin) {
      if (!game?.league_id) {
        returnValue.push(
          <Button onClick={() => editGame()} icon={<EditOutlined />} />
        );
        returnValue.push(
          <Popconfirm
            disabled={deleting}
            onConfirm={() => deleteGame()}
            title={t('questions.delete')}
          >
            <Button
              danger
              disabled={deleting}
              loading={deleting}
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        );
      }
    }

    return returnValue;
  }, [game, deleting, userIsAdmin]);

  const tabs = useMemo(() => {
    const returnValue = [];

    // Club details
    returnValue.push({
      label: t('club_details'),
      key: '1',
      children: (
        <GameDetailsClubComponent
          game={game}
          clubDetails={game?.club_details}
        />
      ),
    });

    // Teams
    if (game?.team_details) {
      game?.team_details.forEach((td) => {
        returnValue.push({
          label: td.team?.name ?? '',
          key: 'td_' + td.team_id,
          children: (
            <GameDetailsTeamComponent
              teamDetails={td}
              homeGame={game?.club_details?.is_home_flag ?? false}
            />
          ),
        });
      });
    }

    // Admin
    if (userIsAdmin) {
      returnValue.push({
        label: t('admin'),
        key: '4',
        children: <GameDetailsAdminComponent game={game} />,
      });
    }

    // Changelog
    returnValue.push({
      label: t('changelog'),
      key: '5',
      children: <EventDetailsAuditsComponent audits={game?.audits} />,
    });

    return returnValue;
  }, [game, userIsAdmin]);

  if (gameState.loadingGameDetails) {
    return <Spin size="large" />;
  }

  if (!game) {
    return null;
  }

  const renderReferees = () => {
    const referee1 = game?.referee_1 ? game?.referee_1 : t('pending');
    const referee2 = game?.referee_2 ? game?.referee_2 : t('pending');
    if (
      game?.club_details?.own_referees &&
      (user?.role.includes(UserRole.BB_ADMIN) ||
        user?.role.includes(UserRole.BB_REFEREE))
    ) {
      return (
        <Space direction="vertical" size={0}>
          <GameDetailsUpdateRefereeComponent game={game} refereeNumber={1} />
          <GameDetailsUpdateRefereeComponent game={game} refereeNumber={2} />
        </Space>
      );
      return null;
    } else {
      return (
        <Space direction="vertical" size={0}>
          <Typography.Text>{referee1}</Typography.Text>
          <Typography.Text>{referee2}</Typography.Text>
        </Space>
      );
    }
  };

  return (
    <Row gutter={gutter}>
      <Col xs={24} lg={8} xxl={6}>
        <Row gutter={gutter}>
          <Col xs={24}>
            <Card>
              <PageHeader
                title={`${game?.home_teamname} - ${game?.guest_teamname}`}
                extra={<Button.Group>{actionButtons}</Button.Group>}
              >
                <Space direction="vertical" size={0}>
                  <Typography.Text>
                    {game?.status === BasketballGameStatus.CANCELED
                      ? t('canceled').toUpperCase()
                      : dayjs(game?.date).format(
                          'ddd ' + dateFormat() + ' - ' + timeFormat()
                        )}
                  </Typography.Text>
                  <Typography.Text>{game?.gym}</Typography.Text>
                </Space>
              </PageHeader>
            </Card>
          </Col>
          <Col xs={24}>
            <Card>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('games.number')}>
                  {game?.game_number || 'T' + game.id}
                </Descriptions.Item>
                <Descriptions.Item label={t('location')}>
                  <Space direction="vertical" size={0}>
                    <Typography.Text>{game?.gym}</Typography.Text>
                    <Typography.Text>
                      {game?.gym_address_lines?.line_1}
                    </Typography.Text>
                    <Typography.Text>
                      {game?.gym_address_lines?.line_2}
                    </Typography.Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t('leagues.singular')}>
                  {game?.league?.name || t('scrimmage_game')}
                </Descriptions.Item>
                <Descriptions.Item label={t('referees.plural')}>
                  {renderReferees()}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </Col>
      <Col xs={24} lg={16} xxl={18}>
        <Card>
          <Tabs items={tabs} />
        </Card>
      </Col>
    </Row>
  );
}
